import React, { ReactNode, useEffect, useState } from 'react';
import { Location, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { useUserContext } from '../../contexts/UserContext';
import { useBackgroundContext } from '../../contexts/BackgroundContext';
import {BasePageContent } from '../BasePage';
import EventPageEvent from '../../components/EventPageEvent';
import { getRSVPedInvitee, hasUserRSVPed, isRSVPVerified } from '../../lib/attendance';
import { endUserEvent } from '../../lib/performance';
import BasePageFooter, { FooterButtonWrapper } from '../BasePageFooter';
import EventCancelledIndicator from '../../components/EventCancelledIndicator';
import EventPageMessages from '../../components/EventPageMessages';
import { useEventCacheContext } from '../../contexts/EventCacheContext';
import { useModalContext } from '../../contexts/ModalContext';
import ShareWithFriendsModal from '../../components/modals/ShareWithFriendsModal';
import RSVPModal from '../../components/modals/RSVPModal';
import ActionButton from '../../components/buttons/ActionButton';
import { updateAttendance, updateAttendanceThroughEmail } from '../../api/ElkEventService';
import useInviteeUuidStorage from '../../hooks/useInviteeUuidStorage';
import BasePageHeader from '../BasePageHeader';
import PhotoPromoModal from '../../components/modals/PhotoPromoModal';
import { b64tos } from '../../util/stringutils';
import DeleteEventModal from '../../components/modals/DeleteEventModal';
import Modal from '../../components/modals/Modal';
import AttendeesList from './AttendeesList';
import AlbumDetails from './AlbumDetails';
import EventCreatedModal from '../../components/modals/EventCreatedModal';
import Mail from '../../components/icons/Mail';
import TextBlast from '../../components/icons/TextBlast';
import FooterLinks from '../../components/FooterLinks';

import { DeviceQueries } from 'Common/src/components/styled';
import LinkIcon from 'Common/src/components/icons/LinkIcon';
import { ULogApplication, ULogSeverity, ULogTag, logSumoEvent } from 'Common/src/api/SumoLogicApi';

import {
  TAppElkAttendeeRole,
  TAppElkAttendeeStatus,
  TAppElkInvitee,
  TAppElkMessage
} from 'TProtocol/prototypes/events/messages';

export interface EventPageState {
  showPhotosPromo: boolean;
  showEventCreated: boolean;
  showRSVP: boolean;
}

interface IUSeparatedMessages {
  blasts: TAppElkMessage[];
  byInviteeId: Map<string, TAppElkMessage[]>;
}

declare const ELK_WEBAPP_URL: string;

const EventPage = () => {
  const userContext = useUserContext();
  const eventCacheContext = useEventCacheContext();
  const backgroundContext = useBackgroundContext();
  const modalContext = useModalContext();
  const { getInviteeUuid, setInviteeUuid, getToken, setToken } = useInviteeUuidStorage();

  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const { eventId } = useParams();
  const location = useLocation() as Location<EventPageState>;
  const isLoggedIn = userContext.isLoggedIn();

  const event = eventId !== undefined ? eventCacheContext.getEvent(eventId) : undefined;
  // const userId = userContext.getAuthHeader().userId;
  const [hasRSVPed, setHasRSVPed] = useState<boolean>(false);
  const [showCopied, setShowCopied] = useState(false);
  const [userSeen, setUserSeen] = useState<boolean>(false);

  const encodedEmailAddress = queryParams.get('ea');
  const inviteeUuidParam = queryParams.get('iu');
  const token = queryParams.get('token') ?? getToken(eventId) ?? undefined;
  const inviteeUuid = inviteeUuidParam ? b64tos(inviteeUuidParam) : getInviteeUuid(eventId);
  const [separatedMessages, setSeparatedMessages] = useState<IUSeparatedMessages>({
    blasts: [],
    byInviteeId: new Map()
  });

  const showCreatedModal = location.state?.showEventCreated && event?.albumDetails !== undefined;

  useEffect(() => {
    if (event?.messages) {
      const separatedMessages: IUSeparatedMessages = {
        blasts: [],
        byInviteeId: new Map()
      };

      for (const message of event.messages) {
        if (message.textBlast) {
          separatedMessages.blasts.push(message);
        } else if (message.receiverInviteeUuid !== undefined) {
          const messagesById = separatedMessages.byInviteeId.get(message.receiverInviteeUuid);

          if (messagesById) {
            messagesById.unshift(message);
          } else {
            separatedMessages.byInviteeId.set(message.receiverInviteeUuid, [message]);
          }
        }
      }

      setSeparatedMessages(separatedMessages);
    }
  }, [event?.messages]);

  const handleTextBlast = () => {
    modalContext.hide();
    navigate('/event/text/' + event?.id);
  };

  const onShare = async () => {
    modalContext.hide();
    if (event?.isHost) {
      onManageInviteClick();
    } else {
      modalContext.show({
        contents: <ShareWithFriendsModal close={modalContext.hide} event={event}/>
      });
    }
  };

  const onShareLink = async () => {
    if (navigator.share) {
      try {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.INFO,
          userId: userContext.id,
          tag: ULogTag.UserAction,
          message: '[EventPage] Clicked on share link with share API'
        });

        await navigator.share({
          title: event?.title ?? 'Shine',
          url: event?.url ?? (event ? `https://${ELK_WEBAPP_URL}/event/${event.id}` : `https://${ELK_WEBAPP_URL}/`),
        });
      } catch (e) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.WARN,
          userId: userContext.id,
          tag: ULogTag.UserError,
          message: `[EventPage] Error during user share: ${JSON.stringify(e)}`
        });
      }
    } else {
      // Fallback for browsers that do not support the Web Share API
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.INFO,
        userId: userContext.id,
        tag: ULogTag.UserAction,
        message: '[EventPage] Clicked on share link without share API'
      });

      modalContext.show({
        contents: <ShareWithFriendsModal close={modalContext.hide} event={event}/>
      });
    }
  };

  const handleRSVP = () => {
    modalContext.show({
      contents: <RSVPModal close={modalContext.hide} event={event} eventId={eventId} clickedYes={false}
                           clickedChangeResponse={true} hasToken={token !== undefined}/>
    });
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0); // Reset to the start of the day

  useEffect(() => {
    if (encodedEmailAddress) {
      userContext.setEmail(b64tos(encodedEmailAddress));
    }
  }, [encodedEmailAddress]);

  useEffect(() => {
    if (event !== undefined) {
      document.title = `Shine - ${event.title}`;
      endUserEvent(userContext);
      backgroundContext.setColors(
        { colors: event?.colors, animation: event?.animation }
      );
    }
  }, [event]);

  useEffect(() => {
    if (eventId !== undefined) {
      void eventCacheContext.fetchEvent({ eventId, inviteeUuid, isPreview: !isLoggedIn, token });
      if (isLoggedIn) {
        // void fetchUserData();
      }
    } else {
      // User came directly to /event without an id, but no event is in context (reload or bookmark)
      navigate('/event/create');
    }
  }, [event?.id, eventId]);

  useEffect(() => {
    if (event) {
      setHasRSVPed(hasUserRSVPed(event, userContext.id ?? '', inviteeUuid));
      if ((userContext.id || inviteeUuid) && !event.messagesArePersonal) {
        let inviteeId: string | undefined = inviteeUuid;
        if (userContext.id) {
          inviteeId = getRSVPedInvitee(event, userContext.id)?.inviteeId;
        }

        void eventCacheContext.refreshMessages(event, inviteeId);
      }
      if (event.albumDetails === undefined) {
        void eventCacheContext.refreshAlbumDetails(event.id, token);
      }
    }
  }, [event?.id, userContext.id]);

  useEffect(() => {
    if (hasRSVPed && event && event.albumDetails?.albumCode === undefined) {
      void eventCacheContext.refreshAlbumDetails(event.id);
    }
  }, [hasRSVPed]);

  useEffect(() => {
    void updateSeen();
  }, [event?.attendees, userContext.id, inviteeUuid]);

  useEffect(() => {
    if (showCopied === true) {
      const timerId = setTimeout(() => {
        setShowCopied(false);
      }, 2000);
      return () => {
        clearTimeout(timerId);
      };
    }
    return () => {
      // noop
    };
  }, [showCopied]);

  useEffect(() => {
    if (location.state?.showRSVP && event) {
      handleRSVP();
    } else if (location.state?.showPhotosPromo && event) {
      modalContext.show({
        contents: <PhotoPromoModal event={event} close={onPhotoPromoClose}
                                   isAdded={isRSVPVerified(event, userContext.id, inviteeUuid)}
                                   onPhoneCodeVerify={onPhotoPromoConfirm}/>
      });
    }
  }, [location.state?.showRSVP, location.state?.showPhotosPromo, event]);

  useEffect(() => {
    if (event && showCreatedModal) {
      modalContext.show({
        contents: <EventCreatedModal
          event={event} close={onEventCreatedModalClose} onLinkClick={onShareLink} onEmailClick={onShare}/>
      });
    }
  }, [showCreatedModal])

  useEffect(() => {
    if (eventId && inviteeUuid) {
      setInviteeUuid(eventId, inviteeUuid);
    }
  }, [eventId, inviteeUuid]);

  useEffect(() => {
    if (eventId && token) {
      setToken(eventId, token);
    }
  }, [eventId, token]);

  const onEventCreatedModalClose = () => {
    navigate(`/event/${eventId}`, { replace: true, state: { showEventCreated: false } });
    modalContext.hide();
  };

  const onPhotoPromoClose = () => {
    navigate(`/event/${eventId}`, { state: { showRSVP: false, showPhotosPromo: false }, replace: true });
    modalContext.hide();
  };

  const onPhotoPromoConfirm = () => {
    navigate(`/event/${eventId}`, { state: { showRSVP: false, showPhotosPromo: false }, replace: true });
  };

  const handleEditClick = () => {
    modalContext.hide();
    if (event === undefined || !event.isHost) {
      return;
    }
      navigate('/event/edit/' + event.id);
  };

  const handleDeleteClick = () => {
    if (eventId) {
      modalContext.show({
        contents: <DeleteEventModal close={modalContext.hide} eventId={eventId}/>
      });
    }
  };

  const handleCloneClick = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[EventPage] Clicked on clone'
    });

    modalContext.show({
      contents: <Modal
        title="Clone Event"
        subtitle="Are you sure you want to clone this event?"
        secondaryButtonLabel="Not now"
        onSecondaryClick={modalContext.hide}
        primaryButtonLabel="Yes, clone event"
        onPrimaryClick={cloneEvent}
        onClickOutside={modalContext.hide}
      />
    })
  };

  const cloneEvent = () => {
    modalContext.hide();
    if (event?.id) {
      navigate(`/event/create/from/${event.id}`);
    }
  }

  const onManageInviteClick = () => {
    modalContext.hide();
    if (eventId !== undefined) {
      navigate(`/event/invite/${eventId}`);
    }
  };

  const eventModule = <EventPageEvent
    event={event}
    handleEditClick={handleEditClick}
    handleDeleteClick={handleDeleteClick}
    handleCloneClick={handleCloneClick}
    handleChangeResponseClick={handleRSVP}
    inviteeUuid={inviteeUuid}
  />;

  const updateSeen = async () => {
    if (eventId === undefined || userSeen || !event || event.attendees.length === 0) {
      return;
    }

    const userId = userContext.id;

    let myInvitee: TAppElkInvitee | undefined;
    if (userContext.isLoggedIn()) {
      myInvitee = event?.attendees
        .filter(attendee => {
            return attendee?.userId === userContext.id || attendee.inviteeId === inviteeUuid;
          }
        )?.[0];

      if (myInvitee !== undefined && myInvitee.rsvpStatus !== TAppElkAttendeeStatus.INVITED) {
        // User already is attending.
        return;
      }

      const userName = await userContext.getUserName();

      if (myInvitee === undefined && userName !== undefined && userName !== null) {
        myInvitee = new TAppElkInvitee({
          inviteeId: uuidv4(),
          rsvpStatus: TAppElkAttendeeStatus.SEEN,
          role: TAppElkAttendeeRole.UNDEFINED,
          additionalGuestCount: 0,
          userId,
          name: userName,
        });
      }
    } else if (inviteeUuid) {
      myInvitee = event?.attendees
        .filter(attendee => {
            return attendee?.inviteeId === inviteeUuid;
          }
        )?.[0];
    }

    if (myInvitee) {
      myInvitee.rsvpStatus = TAppElkAttendeeStatus.SEEN;
      myInvitee.userId = userId;
      setUserSeen(true);
      if (userContext.isLoggedIn()) {
        void updateAttendance(userContext, eventId, myInvitee, undefined, token, false);
      } else if (token) {
        void updateAttendanceThroughEmail(eventId, myInvitee, undefined, token, false);
      }
    }
  };

  const handleOptIn = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[EventPage] Clicked on accept'
    });

    modalContext.show({
      contents: <RSVPModal close={modalContext.hide} event={event} eventId={eventId} clickedYes={true}
                           clickedChangeResponse={false} hasToken={token !== undefined}/>
    });
  };

  const handleOptOut = () => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[EventPage] Clicked on decline'
    });

    modalContext.show({
      contents: <RSVPModal close={modalContext.hide} event={event} eventId={eventId} clickedYes={false}
                           clickedChangeResponse={false} hasToken={token !== undefined}/>
    });
  };

  let footer: ReactNode = <></>;

  if (event?.isHost) {
    if (event.cancelledTimestamp === undefined || event.cancelledTimestamp === 0) {
      footer = <BasePageFooter opacity={0.16} hideBorder={true} isHomePage={false}>
        <FooterButtonWrapper>
          <ActionRow>
            <ActionButton onClick={onShare}>
              <ButtonText>
                <Mail size={40}/><span>Invite by email</span>
              </ButtonText>
            </ActionButton>
            <ActionButton onClick={onShareLink}>
              <ButtonText>
                <LinkIcon/><span>Send a link</span>
              </ButtonText>
            </ActionButton>
            {event.attendees.length > 1 ? <ActionButton onClick={handleTextBlast}>
                <ButtonText>
                  <TextBlast/><span>Message guests</span>
                </ButtonText>
              </ActionButton>
              : null}
          </ActionRow>
        </FooterButtonWrapper>
      </BasePageFooter>;
    }
  } else if (!hasRSVPed) {
    footer = <BasePageFooter opacity={0.16} hideBorder={true} isHomePage={false}>
      <FooterButtonWrapper>
        <ActionButton outline={true} onClick={handleOptIn}>Accept</ActionButton>
        <ActionButton outline={true} onClick={handleOptOut}>Decline</ActionButton>
      </FooterButtonWrapper>
    </BasePageFooter>;
  }

  let messageHistory: ReactNode = null;
  if (separatedMessages.blasts.length > 0) {
    messageHistory = <EventPageMessages messages={separatedMessages.blasts}/>;
  }

  const showAttendeeBox = event && (!event.cancelledTimestamp);

  if (event) {
    return <>
      <BasePageHeader/>
      <BasePageContent $showFooter={true} $wide={true}>
        <ColumnSet id={'inner-content'}>
          <LeftColumn>
            {eventModule}
            {event.cancelledTimestamp ?
              <EventCancelledIndicator cancelledDateString={event?.description ?? ''}/> : <></>}
            {messageHistory}
          </LeftColumn>
          <RightColumn>
            <AlbumDetails event={event}/>
            {showAttendeeBox ? <AttendeesList
              event={event}
              inviteeUuid={inviteeUuid}
              readOnly={false}
              messagesByInviteeId={separatedMessages.byInviteeId}
            /> : null}
          </RightColumn>
        </ColumnSet>
        <FooterLinks/>
      </BasePageContent>
      {footer}
    </>;
  } else {
    return null;
  }
};

const ColumnSet = styled.div`
  display: flex;

  @media not (${DeviceQueries.laptop}) {
    display: contents;
  }
`;

const LeftColumn = styled.div`
  margin: 0 15px;

  @media (${DeviceQueries.laptop}) {
    min-width: 600px;
  }
`;

const RightColumn = styled.div`
  margin: 0 15px;

  @media (${DeviceQueries.laptop}) {
    max-width: 450px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  gap: 15px;
  width: 600px;
`;

const IconHolder = styled.div`
  margin-top: 20px;
`;

const ButtonText = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

export default EventPage;
